'use client';

import { cn } from '@/lib/utils/cn';
import { Check, Minus, X } from 'lucide-react';
import type { IntentAxisScore } from '@/lib/api/types';
import { SectionHeader } from '../shared';

interface AxisBreakdownSectionProps {
  axes: IntentAxisScore[] | null | undefined;
  lineQuery?: string | null;
  label?: string | null;
}

/**
 * Per-axis match breakdown for a product vs the current Intent line.
 * Nine rows (fewer when the intent didn't supply values). Distance
 * 0 = ✓, 1 = ✗, in between = ~. `contribution` tile tells the buyer
 * how much this axis is dragging the match % down.
 */
export function AxisBreakdownSection({
  axes,
  lineQuery,
  label,
}: AxisBreakdownSectionProps) {
  const scored = (axes ?? []).filter((a) => a.distance !== null);
  if (scored.length === 0) return null;
  return (
    <div>
      <SectionHeader title={label ?? 'Match breakdown'} />
      {lineQuery && (
        <p className="text-[11px] text-muted-foreground italic leading-relaxed mb-2">
          {`vs line: ${lineQuery}`}
        </p>
      )}
      <div className="space-y-1">
        {scored.map((a) => (
          <AxisRow key={a.name} axis={a} />
        ))}
      </div>
    </div>
  );
}

function AxisRow({ axis }: { axis: IntentAxisScore }) {
  const status = verdictFor(axis.distance);
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 rounded border text-xs',
        status.wrapperClass,
      )}
    >
      <status.Icon className={cn('h-3.5 w-3.5 flex-shrink-0', status.iconClass)} />
      <div className="min-w-0 flex-1">
        <div className="font-medium">{formatAxisLabel(axis.name)}</div>
        <div className="text-[10px] text-muted-foreground truncate">
          {`intent: ${formatValue(axis.intent_value)} · product: ${formatValue(axis.product_value)}`}
        </div>
      </div>
      <div className="flex-shrink-0 text-right">
        <div className="text-[10px] text-muted-foreground">w {formatNum(axis.weight)}</div>
        <div className="font-mono">{formatContribution(axis.contribution)}</div>
      </div>
    </div>
  );
}

function verdictFor(distance: number | null) {
  if (distance === null || distance === undefined) {
    return {
      Icon: Minus,
      wrapperClass: 'bg-card border-border',
      iconClass: 'text-muted-foreground',
    };
  }
  if (distance <= 0.01) {
    return {
      Icon: Check,
      wrapperClass: 'bg-success/5 border-success/30',
      iconClass: 'text-success',
    };
  }
  if (distance >= 0.99) {
    return {
      Icon: X,
      wrapperClass: 'bg-destructive/5 border-destructive/30',
      iconClass: 'text-destructive',
    };
  }
  return {
    Icon: Minus,
    wrapperClass: 'bg-warning/5 border-warning/30',
    iconClass: 'text-warning',
  };
}

function formatAxisLabel(name: string): string {
  return name.replace(/_/g, ' ');
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  return String(v);
}

function formatNum(n: number): string {
  return Number.isInteger(n) ? n.toString() : n.toFixed(1);
}

function formatContribution(c: number | null): string {
  if (c === null || c === undefined) return '—';
  return c.toFixed(2);
}
